import {
  getLocalReportMeta,
  isConfirmedReportCode,
} from '../config/reportCatalogue';
import type {
  ReportFilterValue,
  SafeReportFilterState,
} from '../types/report';

function validationError(message: string, code = 'VALIDATION_ERROR'): Error {
  return Object.assign(new Error(message), { code });
}

function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

function parseRequiredDate(value: string | undefined, name: string): string {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) throw validationError(`${name} is required`);
  if (!isIsoDate(trimmed)) {
    throw validationError(`${name} must use YYYY-MM-DD format`);
  }
  return trimmed;
}

function parseOptionalPositiveInt(
  value: string | undefined,
  name: string,
): number | null {
  if (value === undefined || value === null || String(value).trim() === '') {
    return null;
  }
  const number = Number(String(value).trim());
  if (!Number.isInteger(number) || number <= 0) {
    throw validationError(`${name} must be a positive integer`);
  }
  return number;
}

function filterText(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') return value.trim();
  return '';
}

function parseYear(value: unknown): number {
  const trimmed = filterText(value);
  if (!trimmed) throw validationError('year is required');
  const number = Number(trimmed);
  if (!Number.isInteger(number) || number < 1000 || number > 9999) {
    throw validationError('year must be a four-digit year');
  }
  return number;
}

function parseMonth(value: unknown): number {
  const trimmed = filterText(value);
  if (!trimmed) throw validationError('month is required');
  const number = Number(trimmed);
  if (!Number.isInteger(number) || number < 1 || number > 12) {
    throw validationError('month must be between 1 and 12');
  }
  return number;
}

function dateRange(state: SafeReportFilterState) {
  const fromDate = parseRequiredDate(state.fromDate, 'fromDate');
  const toDate = parseRequiredDate(state.toDate, 'toDate');
  if (toDate < fromDate) {
    throw validationError('toDate cannot be earlier than fromDate');
  }
  return { fromDate, toDate };
}

/**
 * Validate filter form state for a confirmed report code.
 * Rejects unknown codes and free-text procedure names.
 */
export function validateReportFilters(
  reportCode: string,
  state: SafeReportFilterState,
): string | null {
  try {
    normalizeReportFilters(reportCode, state);
    return null;
  } catch (error) {
    if (error instanceof Error) return error.message;
    return 'Check the selected report filters.';
  }
}

/**
 * Normalize filters to the exact backend run-report body shape.
 * Only confirmed fields are included; empty optional values become null/omitted.
 */
export function normalizeReportFilters(
  reportCode: string,
  state: SafeReportFilterState,
): Record<string, ReportFilterValue> {
  if (!isConfirmedReportCode(reportCode)) {
    throw validationError('Report code is not supported', 'INVALID_REPORT_CODE');
  }

  // Reject free-text procedure / SQL style keys if somehow present.
  const banned = Object.keys(state).some(key =>
    /procedure|sql|query|filepath|organizationid|createdby/i.test(key),
  );
  if (banned) {
    throw validationError('Unsupported filter fields were provided');
  }

  switch (reportCode) {
    case 'ATTENDANCE_REGISTER':
    case 'LEAVE_REGISTER':
    case 'LATE_ARRIVAL_DEDUCTIONS':
      return {
        ...dateRange(state),
        departmentId: parseOptionalPositiveInt(
          state.departmentId,
          'departmentId',
        ),
      };
    case 'DAILY_ATTENDANCE_SUMMARY':
      return {
        attendanceDate: parseRequiredDate(
          state.attendanceDate,
          'attendanceDate',
        ),
      };
    case 'MONTHLY_ATTENDANCE_SUMMARY':
    case 'MONTHLY_LOP':
      return {
        year: parseYear(state.year),
        month: parseMonth(state.month),
      };
    case 'EMPLOYEE_DIRECTORY':
      return {};
    case 'LEAVE_BALANCE':
      return { year: parseYear(state.year) };
    case 'WFH_SUMMARY':
    case 'PERMISSION_SUMMARY':
      return dateRange(state);
    default:
      throw validationError(
        'Report code is not supported',
        'INVALID_REPORT_CODE',
      );
  }
}

export function buildReportRequest(
  reportCode: string,
  state: SafeReportFilterState,
  format: 'JSON' | 'CSV' | 'XLSX',
): {
  format: 'JSON' | 'CSV' | 'XLSX';
  download: boolean;
  filters: Record<string, ReportFilterValue>;
} {
  const filters = normalizeReportFilters(reportCode, state);
  return {
    format,
    download: format !== 'JSON',
    filters,
  };
}

export function createDefaultReportFilterState(
  reportCode: string,
  now = new Date(),
): SafeReportFilterState {
  const meta = getLocalReportMeta(reportCode);
  if (!meta) return {};

  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1);
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  const firstOfMonth = `${year}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

  const state: SafeReportFilterState = {};
  for (const filter of meta.filters) {
    switch (filter.field) {
      case 'fromDate':
        state.fromDate = firstOfMonth;
        break;
      case 'toDate':
        state.toDate = today;
        break;
      case 'attendanceDate':
        state.attendanceDate = today;
        break;
      case 'year':
        state.year = year;
        break;
      case 'month':
        state.month = month;
        break;
      case 'departmentId':
        state.departmentId = '';
        break;
      default:
        state[filter.field] = '';
    }
  }
  return state;
}

export function summarizeReportFilters(
  reportCode: string,
  state: SafeReportFilterState,
): string {
  const meta = getLocalReportMeta(reportCode);
  if (!meta || meta.filters.length === 0) return 'No filters';

  const parts: string[] = [];
  for (const filter of meta.filters) {
    const value = state[filter.field];
    if (value === undefined || value === null || String(value).trim() === '') {
      continue;
    }
    parts.push(`${filter.label}: ${String(value).trim()}`);
  }
  return parts.length ? parts.join(' · ') : 'No filters';
}
